import { Link } from "@tanstack/react-router";
import { useEffect, useRef, useState, type ReactNode } from "react";
import type { HomeSituation } from "@/data/home-situations";
import { useReader } from "@/features/blog/use-reader";
import { nextVisitCounter, pickNextStep, type NextStepVariant } from "@/lib/home-next-step";
import { trackEvent } from "@/lib/analytics";
import { btn } from "@/components/ds/styles";
import { ClosingBand as Frame, ClosingOthers as Others } from "@/components/ds/closing-band";

import denim from "@/assets/dr-sara-denim-circle.jpg.asset.json";

const photo = { src: denim.url, alt: "د. سارة النحّاس", width: 520, height: 520 };

const primary: string = btn.inversePrimary;
const onLight: string = btn.primary;
const secondaryLink: string = btn.inverseLink;
const lightLink: string = "inline-flex min-h-11 items-center font-display text-[15px] font-bold underline-offset-4 hover:underline";

type Props = {
  situation: HomeSituation | null;
  duration: number | string;
  price: string;
  pkgPrice: string;
  whatsappUrl: string | null;
};

/** Home closing «next step»: SSR shows the brown band; the client picks one variant after mount. */
export function NextStepCta({ situation, duration, price, pkgPrice, whatsappUrl }: Props) {
  const { reader } = useReader();
  const readerActive = Object.values(reader.posts).some((p) => p.read_at || p.saved_at);
  const counter = useRef<number | null>(null);
  const [mounted, setMounted] = useState(false);

  useEffect(() => {
    counter.current = nextVisitCounter(typeof window !== "undefined" ? window.sessionStorage : null);
    setMounted(true);
  }, []);

  const variant: NextStepVariant = mounted
    ? pickNextStep({ situation: Boolean(situation), readerActive, counter: counter.current ?? 0, hasWhatsApp: Boolean(whatsappUrl) })
    : "brown";

  useEffect(() => {
    if (mounted) trackEvent("cta_view", { variant });
  }, [mounted, variant]);
  const click = () => trackEvent("cta_click", { variant });

  const book = (label = "حجز استشارة ←", cls = primary) => (
    <Link to="/book" onClick={click} className={cls}>{label}</Link>
  );
  const bousala = (cls = secondaryLink) => (
    <Link to="/bousala" onClick={click} className={cls}>بدء البوصلة مجاناً ←</Link>
  );
  const wa = (cls = secondaryLink) =>
    whatsappUrl ? (
      <a href={whatsappUrl} target="_blank" rel="noopener noreferrer" onClick={click} className={cls}>سؤال على واتساب ←</a>
    ) : null;
  const blog = (cls = secondaryLink) => (
    <Link to="/blog" onClick={click} className={cls}>متابعة مسار القراءة ←</Link>
  );

  let body: ReactNode;
  if (variant === "situation" && situation) {
    body = (
      <Frame photo={photo} tone="maroon" eyebrow={`لموقفك اليوم: ${situation.chip}`} title="هالموقف إلو حل، وإلو خطة." line={`${duration} دقيقة مع د. سارة عن هالموقف بالذات، وخطة عملية لبيتكم. اليوم والساعة بنتفق عليهم على واتساب.`}>
        <div className="flex flex-col gap-3 lg:flex-row lg:items-center lg:gap-5">
          {book()}
          <Link to="/blog/$slug" params={{ slug: situation.slug }} onClick={click} className={secondaryLink}>
            قراءة المقال أولاً ←
          </Link>
        </div>
        <Others>{bousala()}{wa()}</Others>
      </Frame>
    );
  } else if (variant === "reader") {
    body = (
      <Frame photo={photo}
        tone="maroon"
        title="المقالات بتعطي الفكرة، والجلسة بتعطي خطة لبيتكم."
        line={`${duration} دقيقة مع د. سارة عن طفلك بالذات، واليوم والساعة بنتفق عليهم على واتساب.`}
      >
        <div className="flex flex-col gap-3 lg:flex-row lg:items-center lg:gap-5">
          {book()}
          {blog(secondaryLink)}
        </div>
        <Others>{bousala()}{wa()}</Others>
      </Frame>
    );
  } else if (variant === "green") {
    body = (
      <Frame photo={photo}
        tone="green"
        eyebrow="مجاناً"
        title="عندكم ابن أو بنت بعمر الاختيار؟"
        line="البوصلة الدراسية بتكشف التخصص الأقرب بـ8 محاور، والتقرير بيطلع فوراً."
      >
        {bousala(onLight)}
        <Others>{book("حجز استشارة ←", lightLink)}{wa(lightLink)}</Others>
      </Frame>
    );
  } else if (variant === "amber" && whatsappUrl) {
    body = (
      <Frame photo={photo} tone="amber" title="سؤال قصير قبل الحجز؟" line="واتساب العيادة مفتوح للأسئلة عن الجلسات والدفع والمواعيد.">
        {wa(onLight)}
        <Others>{book("حجز استشارة ←", lightLink)}{bousala(lightLink)}</Others>
      </Frame>
    );
  } else {
    body = (
      <Frame photo={photo}
        tone="maroon"
        title="كل طفل رسالته مختلفة. خلّينا نفهمها سوا."
        line={`${duration} دقيقة مع د. سارة، أونلاين من أي بلد أو بالعيادة، وخطة عملية لبيتكم. اليوم والساعة بنتفق عليهم على واتساب.`}
        price={price || null}
        priceNote={`للجلسة${pkgPrice ? ` · 4 جلسات بـ ${pkgPrice}` : ""}`}
      >
        {book()}
        <Others>{bousala()}{wa()}</Others>
      </Frame>
    );
  }

  return (
    <div className="min-h-[440px] lg:min-h-[340px]">
      <div key={variant} className={mounted ? "ds-rise" : ""}>{body}</div>
    </div>
  );
}

